import {useMemo} from 'react'
import {Button, Checkbox, Dropdown, Empty, Flex, Space, Table, Tag, Typography,} from 'antd'
import {FilterOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import {useIntl} from 'react-intl'

const GraphMissingImagesTable = ({
  dataSource,
  pagination = false,
  getGraphMissingImagesList = null,
  isMissingConnectorOrTerminalImg = false,
  isMissingVehicleImg = false,
  onChangeIsMissingConnectorOrTerminalImg = () => {},
  onChangeIsMissingVehicleImg = () => {},
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'graphMissingImages.componentId' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'component_id',
        render: (text) => (
          <EllipsisTooltip className="multiline-ellipsis" lineClamp={2}>{text || '-'}</EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'graphMissingImages.description' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'component_desc',
        render: (text) => (
          <EllipsisTooltip className="multiline-ellipsis" lineClamp={2}>{text || '-'}</EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'graphMissingImages.harness' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'harness',
        render: (text) => (
          <EllipsisTooltip className="multiline-ellipsis" lineClamp={2}>{text || '-'}</EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'graphMissingImages.vin' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'vin',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">{text || '-'}</EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'graphMissingImages.category' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'category',
        render: (text) => text || '-',
      },
      {
        title: (
          <Flex gap={4} align="center" justify="space-between">
            <EllipsisTooltip className="ellipsis">
              {intl.formatMessage({ id: 'graphMissingImages.missingImages' })}
            </EllipsisTooltip>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: 'is_missing_connector_or_terminal_img',
                    label: (
                      <Checkbox
                        checked={isMissingConnectorOrTerminalImg}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          onChangeIsMissingConnectorOrTerminalImg(
                            e.target.checked
                          )
                        }}
                      >
                        {intl.formatMessage({
                          id: 'graphMissingImages.connectorOrTerminal',
                        })}
                      </Checkbox>
                    ),
                  },
                  {
                    key: 'is_missing_vehicle_img',
                    label: (
                      <Checkbox
                        checked={isMissingVehicleImg}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          onChangeIsMissingVehicleImg(e.target.checked)
                        }}
                      >
                        {intl.formatMessage({
                          id: 'graphMissingImages.vehicleImages',
                        })}
                      </Checkbox>
                    ),
                  },
                ],
              }}
            >
              <Button
                type="text"
                size="small"
                icon={
                  <FilterOutlined
                    style={{
                      color:
                        isMissingConnectorOrTerminalImg || isMissingVehicleImg
                          ? '#1677ff'
                          : undefined,
                    }}
        scroll={dataSource?.length ? { x: 1000 } : undefined}
      />
                }
              />
            </Dropdown>
          </Flex>
        ),
        dataIndex: 'missing_images',
        align: 'right',
        render: (_, record) => {
          const missingConnectorOrTerminalImg = `${intl.formatMessage({ id: 'graphMissingImages.connectorOrTerminal', })} : ${record.is_missing_connector_or_terminal_img ?? '-'}`
          const missingVehicleImg = `${intl.formatMessage({ id: 'graphMissingImages.vehicleImages', })} : ${record.is_missing_vehicle_img ?? '-'}`

          return (
            <Space size={0} className="missing-images-tags">
              <Tag color='orange'>
                  {missingConnectorOrTerminalImg}
              </Tag>
              <Tag color="red">
                  {missingVehicleImg}
              </Tag>
            </Space>
          )
        },
      },
    ],
    [
      intl,
      isMissingConnectorOrTerminalImg,
      isMissingVehicleImg,
      onChangeIsMissingConnectorOrTerminalImg,
      onChangeIsMissingVehicleImg,
    ]
  )
  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `graph-missing-images-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        scroll={dataSource?.length ? { x: 750 } : undefined}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getGraphMissingImagesList?.(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default GraphMissingImagesTable
